import { forwardRef, type HTMLAttributes } from "react";
import { Badge } from "./Badge";
import { type DisciplineSlug, DISCIPLINES, LEVELS_PER_DISCIPLINE } from "@jumpbean/shared";

export interface EarnedBadgeInfo {
  disciplineSlug: DisciplineSlug;
  levelNumber: number;
  earnedAt: string;
}

export interface BadgeShowcaseProps extends HTMLAttributes<HTMLDivElement> {
  /** Array of earned badges */
  earnedBadges: EarnedBadgeInfo[];
  /** Maximum number of badges to display */
  maxDisplay?: number;
  /** Size of badges */
  size?: "sm" | "md";
  /** Whether to show a count label */
  showCount?: boolean;
  /** Callback when the showcase is clicked to expand */
  onExpand?: () => void;
}

/**
 * BadgeShowcase - A compact display of recently earned badges.
 * Perfect for profile cards and summary views.
 *
 * Features:
 * - Shows most recent badges first
 * - Overlapping badge layout for compact display
 * - Optional expand action for viewing all badges
 * - Shows total count if more badges exist
 *
 * @example
 * // On profile card
 * <BadgeShowcase
 *   earnedBadges={childBadges}
 *   maxDisplay={5}
 *   onExpand={() => navigate(`/profiles/${childId}/badges`)}
 * />
 */
export const BadgeShowcase = forwardRef<HTMLDivElement, BadgeShowcaseProps>(
  (
    {
      earnedBadges,
      maxDisplay = 5,
      size = "sm",
      showCount = true,
      onExpand,
      className = "",
      ...props
    },
    ref
  ) => {
    // Sort by most recent first
    const sortedBadges = [...earnedBadges].sort(
      (a, b) => new Date(b.earnedAt).getTime() - new Date(a.earnedAt).getTime()
    );

    const displayBadges = sortedBadges.slice(0, maxDisplay);
    const remainingCount = Math.max(0, earnedBadges.length - maxDisplay);
    const totalPossible = Object.keys(DISCIPLINES).length * LEVELS_PER_DISCIPLINE;

    // Empty state
    if (earnedBadges.length === 0) {
      return (
        <div
          ref={ref}
          className={`
            flex items-center gap-2
            ${className}
          `}
          {...props}
        >
          <div className="flex items-center gap-1">
            {/* Show 3 placeholder badges */}
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className={`
                  ${size === "sm" ? "w-8 h-8" : "w-10 h-10"}
                  rounded-full
                  bg-neutral-200
                  border-2 border-neutral-300
                  flex items-center justify-center
                  ${i > 1 ? "-ml-2" : ""}
                `}
              >
                <LockMiniIcon className="w-3 h-3 text-neutral-400" />
              </div>
            ))}
          </div>
          <span className="font-body text-body-sm text-neutral-500">
            No badges yet
          </span>
        </div>
      );
    }

    const sharedClassName = `
      flex items-center gap-3
      ${onExpand ? "cursor-pointer group focus:outline-none focus-visible:ring-4 focus-visible:ring-fairy-400 rounded-full pr-2" : ""}
      ${className}
    `;

    const content = (
      <>
        {/* Stacked badges */}
        <div className="flex items-center">
          {displayBadges.map((badge, index) => (
            <div
              key={`${badge.disciplineSlug}-${badge.levelNumber}`}
              className={`
                relative
                ${index > 0 ? "-ml-3" : ""}
                transition-transform duration-200
                ${onExpand ? "group-hover:translate-x-1" : ""}
              `}
              style={{ zIndex: displayBadges.length - index }}
            >
              <Badge
                discipline={badge.disciplineSlug}
                level={badge.levelNumber}
                isEarned
                size={size}
              />
            </div>
          ))}

          {/* +N more indicator */}
          {remainingCount > 0 && (
            <div
              className={`
                relative -ml-2
                ${size === "sm" ? "w-8 h-8" : "w-10 h-10"}
                rounded-full
                bg-sunshine-100
                border-2 border-sunshine-300
                flex items-center justify-center
                font-display font-bold
                ${size === "sm" ? "text-xs" : "text-sm"}
                text-sunshine-700
                shadow-sm
              `}
              style={{ zIndex: 0 }}
            >
              +{remainingCount}
            </div>
          )}
        </div>

        {/* Badge count */}
        {showCount && (
          <div className="flex items-center gap-1.5">
            <TrophyIcon className="w-4 h-4 text-sunshine-500" />
            <span className="font-display font-bold text-body-sm text-neutral-700">
              {earnedBadges.length}
            </span>
            <span className="font-body text-body-xs text-neutral-500">
              / {totalPossible}
            </span>
          </div>
        )}

        {/* Expand arrow */}
        {onExpand && (
          <ChevronIcon className="w-4 h-4 text-neutral-400 group-hover:text-neutral-600 transition-colors" />
        )}
      </>
    );

    if (onExpand) {
      return (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          onClick={onExpand}
          type="button"
          className={sharedClassName}
          aria-label={`View all ${earnedBadges.length} badges`}
        >
          {content}
        </button>
      );
    }

    return (
      <div
        ref={ref}
        className={sharedClassName}
        {...props}
      >
        {content}
      </div>
    );
  }
);

BadgeShowcase.displayName = "BadgeShowcase";

// Mini lock icon
function LockMiniIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

// Trophy icon
function TrophyIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M5 3h14a1 1 0 0 1 1 1v2a5 5 0 0 1-5 5h-1v4h4a1 1 0 0 1 1 1v4H5v-4a1 1 0 0 1 1-1h4v-4H9a5 5 0 0 1-5-5V4a1 1 0 0 1 1-1zm1 2v1a3 3 0 0 0 3 3h6a3 3 0 0 0 3-3V5H6z" />
    </svg>
  );
}

// Chevron icon
function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

export default BadgeShowcase;
